import { cn } from '@/utils';
import { CSSProperties, FC } from 'react';

export interface LoaderProps {
  size?: number;
  duration?: number;
  label?: string;
  caption?: string;
  className?: string;
  style?: CSSProperties;
}

const BASE = 110;

export const Loader: FC<LoaderProps> = ({
  size = BASE,
  duration = 0.5,
  label = 'Loading',
  className,
  style,
}) => {
  const scale = size / BASE;

  const vars: CSSProperties = {
    '--loader-size': `${size}px`,
    '--loader-radius': `${16 * scale}px`,
    '--loader-border-width': `${Math.max(2, 2 * scale)}px`,
    '--dot-size': `${22 * scale}px`,
    '--dot-rest': `${17 * scale}px`,
    '--dot-near': `${5 * scale}px`,
    '--loader-duration': `${duration}s`,
  } as CSSProperties;

  return (
    <div
      className={cn(`absolute inset-0 z-50 `, className)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        ...style,
      }}
    >
      <div className="custom-loader-card" style={vars} role="status" aria-label={label}>
        <span className="custom-loader-dots">
          <span className="custom-loader-dot custom-loader-dot--left" />
          <span className="custom-loader-dot custom-loader-dot--right" />
        </span>
      </div>

      <style>{`
        .custom-loader-card {
          width: var(--loader-size);
          height: var(--loader-size);
          border-radius: var(--loader-radius);
          background: #30353A;
          border: var(--loader-border-width) solid #dbaa0b;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .custom-loader-dots {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .custom-loader-dot {
          position: absolute;
          width: var(--dot-size);
          height: var(--dot-size);
          border-radius: 50%;
          background: #fcc614;
          will-change: transform, opacity;
        }

        .custom-loader-dot--left {
          animation: custom-loader-left var(--loader-duration) cubic-bezier(0.45, 0, 0.55, 1) infinite;
        }

        .custom-loader-dot--right {
          animation: custom-loader-right var(--loader-duration) cubic-bezier(0.45, 0, 0.55, 1) infinite;
        }

        @keyframes custom-loader-left {
          0%   { transform: translateX(calc(-1.5 * var(--dot-rest))); opacity: 1; }
          50%  { transform: translateX(var(--dot-near)); opacity: 0.42; }
          100% { transform: translateX(calc(-1.5 * var(--dot-rest))); opacity: 1; }
        }

        @keyframes custom-loader-right {
          0%   { transform: translateX(var(--dot-rest)); opacity: 0.42; }
          50%  { transform: translateX(calc(-1.5 * var(--dot-near))); opacity: 1; }
          100% { transform: translateX(var(--dot-rest)); opacity: 0.42; }
        }

        .custom-loader-caption {
          color: #CFD3D7;
          font-size: 0.813rem;
          letter-spacing: 0.02em;
        }

        @media (prefers-reduced-motion: reduce) {
          .custom-loader-dot--left,
          .custom-loader-dot--right {
            animation-duration: calc(var(--loader-duration) * 2);
          }
        }
      `}</style>
    </div>
  );
};

export default Loader;
